import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, Table, Tooltip } from 'antd'
import moment from 'moment'
import shallow from 'zustand/shallow'

import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import PerSpacer from '../Spacer'
import { useAuth } from '../../../context/auth-context'
import { PerTag } from '../Tag'
import { PerEmpty } from '../Empty'
import { useDateTime } from '../../../shared/useDateTime'
import { useEditForecastPermission } from '../../../modules/pyramids/usePermission'
import { PerAvatarPlus } from '../AvatarPlus'
import { rankingUiProps } from '../../../modules/cultivatingTalents/ranking.uiprops'
import { CareerPathPopover } from '../CarrerPathPopover/CareerPathPopover'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { usePyramidsStore, PyramidTabs } from '../../../modules/pyramids/PyramidsStore'
import { filterCareerPathReferencesByLevel } from '../../../modules/appraisal/contract/careerPath/careerpath-util'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { CareerPathEvaluationState, Currency, EmployeeLevelEnum, EmployeeTagType, PyramidForecastDto } from '../../../types'
import { useForecastModalStore, ForecastModalEmployeeTableDatePicker, ForecastModalEmployeeTableSelect, ForecastModalUndoButton } from '.'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'

const { Option } = Select

const pathSorter = [CareerPathEvaluationState.Inline, CareerPathEvaluationState.OutOfLine, 'None']

export const ForecastModalEmployeeTable: FC<{ isEmployeeLevelValidationEnabled: boolean }> = ({ isEmployeeLevelValidationEnabled }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { formatDateShortWithTimezone } = useDateTime(user)
  const { tagColor, isPerformanceTag } = rankingUiProps()

  const [tableData, employeesSelectedRows, filterEmployeeLevels, careerPath, filterBu, setEmployeeSelectedRows] = useForecastModalStore(
    (state) => [
      state.employeeData,
      state.employeesSelectedRows,
      state.filterEmployeeLevels,
      state.careerPath,
      state.filterBu,
      state.setEmployeeSelectedRows,
    ],
    shallow,
  )

  const [companyId, pyramidActiveTab, isAgePyramidVisible, isCareerPathEnabled] = usePyramidsStore(
    (state) => [state.selected.companyId, state.activeTab, state.isAgePyramidVisible, state.isCareerPathEnabled],
    shallow,
  )

  const hasPermission = useEditForecastPermission(companyId)

  const renderEmployeeLevelBasedOnCurrentEmployeeLevel = (
    currentEmployeeLevel: EmployeeLevelEnum,
    employeeLevels: EmployeeLevelEnum[],
    isEmployeeLevelValidationEnabled: boolean,
  ) => {
    const idx = employeeLevels.indexOf(currentEmployeeLevel)
    const validLevels = idx > -1 ? (isEmployeeLevelValidationEnabled ? employeeLevels.slice(idx + 1) : employeeLevels) : []
    if (
      isEmployeeLevelValidationEnabled &&
      employeeLevels.indexOf(EmployeeLevelEnum.Spr) >= 0 &&
      currentEmployeeLevel === EmployeeLevelEnum.Sm
    ) {
      validLevels.unshift(EmployeeLevelEnum.Spr)
    } else if (
      isEmployeeLevelValidationEnabled &&
      employeeLevels.indexOf(EmployeeLevelEnum.Pr) >= 0 &&
      currentEmployeeLevel === EmployeeLevelEnum.M
    ) {
      validLevels.unshift(EmployeeLevelEnum.Pr)
    }
    return validLevels
      .filter((level: EmployeeLevelEnum) => level !== EmployeeLevelEnum.Leaver)
      .map((level: EmployeeLevelEnum) => (
        <Option key={level} value={level} disabled={level === currentEmployeeLevel}>
          {level}
        </Option>
      ))
  }

  const calculateTimeDifferenceFromNow = (date: string, prefix: string) => {
    const duration = moment.duration(moment().diff(date))

    const yearsValue = Math.abs(duration.get('years'))
    const monthsValue = Math.abs(duration.get('months'))
    const daysValue = Math.abs(duration.get('days'))

    const yearsLocalised = moment.duration(yearsValue, 'years').locale('en').humanize()
    const monthsLocalised = moment.duration(monthsValue, 'months').locale('en').humanize({ M: 12 })
    const daysLocalised = moment.duration(daysValue, 'days').locale('en').humanize({ d: 31 })

    const timeDiff = [
      { name: yearsLocalised, value: yearsValue },
      { name: monthsLocalised, value: monthsValue },
      { name: daysLocalised, value: daysValue },
    ]
      .filter((timeDiff) => !!timeDiff.value)
      .reduce((acc: string[], current, index) => {
        if (index < 2) {
          acc.push(`${current.name}`)
        }
        return acc
      }, [])
      .join(', ')

    return `${prefix} ${timeDiff}`
  }

  const rowSelection = {
    onChange: (rowIds) => {
      setEmployeeSelectedRows(rowIds)
    },
    selectedRowKeys: employeesSelectedRows,
    getCheckboxProps: (employee: PyramidForecastDto) => ({
      disabled:
        (!!employee.tags &&
          employee.tags.some(
            (tag) => tag === EmployeeTagType.Low || tag === EmployeeTagType.Leaver || tag === EmployeeTagType.EntryLevelLow,
          )) ||
        !hasPermission ||
        pyramidActiveTab === PyramidTabs.Age,
    }),
  }

  const { tableActionButtonsRowWidth } = tableActionButtons()

  const employeeTableHeader = (
    <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('pyramidsView.forecastModal.table.employee')}</Tooltip>
  )

  const columns = [
    {
      key: 'forecast-employee',
      title: employeeTableHeader,
      className: 'minWidth-column-xl',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.employeeFullName ? rowA.employeeFullName : ''
        const valueB = !!rowB.employeeFullName ? rowB.employeeFullName : ''
        return valueA.localeCompare(valueB)
      },
      render: (record: PyramidForecastDto) => {
        return (
          <PerAvatarPlus
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            fullName={record.employeeFullName || ''}
            employeeId={record.employeeId}
          />
        )
      },
    },
    {
      key: 'forecast-bu',
      title: t('pyramidsView.forecastModal.table.bu'),
      className: 'minWidth-column-s',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.businessUnitName ? rowA.businessUnitName : ''
        const valueB = !!rowB.businessUnitName ? rowB.businessUnitName : ''
        return valueA.localeCompare(valueB)
      },
      render: (record: PyramidForecastDto) => {
        const bu = filterBu.find((bu) => bu.id === record.businessUnitId)
        return <PerText className={!!bu && !!bu.isExpired ? 'bu-expired' : undefined}>{record.businessUnitName}</PerText>
      },
    },
    {
      key: 'forecast-level',
      title: t('pyramidsView.forecastModal.table.level'),
      className: 'minWidth-column-s',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.currentEmployeeLevel ? rowA.currentEmployeeLevel : ''
        const valueB = !!rowB.currentEmployeeLevel ? rowB.currentEmployeeLevel : ''
        return valueA.localeCompare(valueB)
      },
      render: (record: PyramidForecastDto) => {
        return <PerText>{record.currentEmployeeLevel}</PerText>
      },
    },
    {
      key: 'forecast-salary',
      title: t('pyramidsView.forecastModal.table.salary'),
      className: 'minWidth-column-m textAlign-right',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.salary ? rowA.salary : 0
        const valueB = !!rowB.salary ? rowB.salary : 0
        return valueA - valueB
      },
      render: (record: PyramidForecastDto) => {
        return <PerText>{record.salary ? formatCurrency(record.salary, record.currencyForPayments || '', true) : ''}</PerText>
      },
    },
    {
      key: 'forecast-bonus',
      title: t('pyramidsView.forecastModal.table.bonus'),
      className: 'minWidth-column-m textAlign-right',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.bonus ? rowA.bonus : 0
        const valueB = !!rowB.bonus ? rowB.bonus : 0
        return valueA - valueB
      },
      render: (record: PyramidForecastDto) => {
        return <PerText>{record.bonus ? formatCurrency(record.bonus, record.currencyForPayments || '', true) : ''}</PerText>
      },
    },
    {
      key: 'forecast-path',
      title: t('pyramidsView.forecastModal.table.path'),
      className: 'minWidth-column-s',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.careerPath ? rowA.careerPath.summary : 'None'
        const valueB = !!rowB.careerPath ? rowB.careerPath.summary : 'None'
        return pathSorter.indexOf(valueA) - pathSorter.indexOf(valueB)
      },
      render: (record: PyramidForecastDto) => {
        const reference =
          !!careerPath && filterCareerPathReferencesByLevel(careerPath, record.currentEmployeeLevel || EmployeeLevelEnum.None)
        return (
          !!record.careerPath &&
          !!reference && (
            <CareerPathPopover
              careerPath={record.careerPath}
              bonusReference={reference.bonus}
              salaryReference={reference.salary}
              currency={record.currencyForPayments || Currency.Eur}
            />
          )
        )
      },
    },
    {
      key: 'forecast-tags',
      title: t('pyramidsView.forecastModal.table.tags'),
      className: 'minWidth-column-m',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.tags && !!rowA.tags.length ? rowA.tags.length : 0
        const valueB = !!rowB.tags && !!rowB.tags.length ? rowB.tags.length : 0
        return valueA - valueB
      },
      render: (record: PyramidForecastDto) => {
        const tags =
          !!record.tags &&
          record.tags
            .filter((tag) => isPerformanceTag(tag) || tag === EmployeeTagType.Leaver || tag === EmployeeTagType.Limited)
            .map((tag, index) => {
              return (
                <React.Fragment key={index}>
                  <PerSpacer mt={!!index ? 'xxxs' : 'none'} />
                  <PerTag color={tagColor(tag)} style={{ width: '80px', marginRight: 0 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(`global.employeeTags.general.${tag}`)}</div>
                  </PerTag>
                </React.Fragment>
              )
            })
        return (
          <PerFlexRow direction="column" justify="center">
            {tags}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'forecast-age',
      title: t('pyramidsView.forecastModal.table.age'),
      className: 'minWidth-column-s',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.age ? rowA.age : 0
        const valueB = !!rowB.age ? rowB.age : 0
        return valueA - valueB
      },
      render: (record: PyramidForecastDto) => {
        return <PerText>{record.age}</PerText>
      },
    },
    {
      key: 'forecast-startDate',
      title: t('pyramidsView.forecastModal.table.startDate'),
      className: 'minWidth-column-m',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.startDate ? moment(rowA.startDate).valueOf() : 0
        const valueB = !!rowB.startDate ? moment(rowB.startDate).valueOf() : 0
        return valueA - valueB
      },
      render: (record: PyramidForecastDto) => {
        return (
          <PerFlexRow direction="column">
            <PerText>{formatDateShortWithTimezone(record.startDate, record.employeeTimezone as string)}</PerText>
            <PerText color="secondary" fontSize="xs">
              {calculateTimeDifferenceFromNow(record.startDate, t('pyramidsView.forecastModal.timeDiff.since'))}
            </PerText>
          </PerFlexRow>
        )
      },
    },
    {
      key: 'forecast-endDate',
      title: t('pyramidsView.forecastModal.table.endDate'),
      className: 'minWidth-column-l',
      sorter: (rowA: PyramidForecastDto, rowB: PyramidForecastDto) => {
        const valueA = !!rowA.endDate ? moment(rowA.endDate).valueOf() : 0
        const valueB = !!rowB.endDate ? moment(rowB.endDate).valueOf() : 0
        return valueA - valueB
      },
      render: (record: PyramidForecastDto) => {
        return record.endDate ? (
          <PerFlexRow direction="column">
            <PerText>{formatDateShortWithTimezone(record.endDate, record.employeeTimezone as string)}</PerText>
            <PerText color="secondary" fontSize="xs">
              {calculateTimeDifferenceFromNow(record.endDate, t('pyramidsView.forecastModal.timeDiff.leavesIn'))}
            </PerText>
          </PerFlexRow>
        ) : (
          <ForecastModalEmployeeTableDatePicker employeeId={record.employeeId} user={user} dateColumnName="forecastEndDate" />
        )
      },
    },
    {
      key: 'forecast-levelChange',
      title: t('pyramidsView.forecastModal.table.levelChange'),
      className: 'minWidth-column-l',
      render: (record: PyramidForecastDto) => {
        return (
          <ForecastModalEmployeeTableSelect employeeId={record.employeeId} placeholder={t('pyramidsView.forecastModal.common.selectLevel')}>
            {renderEmployeeLevelBasedOnCurrentEmployeeLevel(
              record.currentEmployeeLevel || EmployeeLevelEnum.Leaver,
              filterEmployeeLevels,
              isEmployeeLevelValidationEnabled,
            )}
          </ForecastModalEmployeeTableSelect>
        )
      },
    },
    {
      key: 'forecastDate',
      title: t('pyramidsView.forecastModal.table.forecastDate'),
      className: 'minWidth-column-l',
      render: (record: PyramidForecastDto) => {
        return <ForecastModalEmployeeTableDatePicker employeeId={record.employeeId} user={user} dateColumnName="forecastDate" />
      },
    },
    {
      key: 'forecast-actions',
      title: t('pyramidsView.forecastModal.table.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(1),
      render: (record: PyramidForecastDto) => {
        return <ForecastModalUndoButton employeeId={record.employeeId} />
      },
    },
  ]
    .filter((column) => column.key !== 'forecast-path' || isCareerPathEnabled)
    .filter((column) => column.key !== 'forecast-age' || isAgePyramidVisible)

  return (
    <PerFlexRow mt="l">
      <Table
        columns={columns}
        dataSource={tableData}
        rowSelection={rowSelection}
        pagination={false}
        //rowClassName={() => 'minHeight-row'}
        rowKey="employeeId"
        className="fullWidth small"
        rowClassName={() => 'minHeight-row'}
        locale={{
          emptyText: (
            <PerEmpty
              heading={t('pyramidsView.forecastModal.common.noEmployee')}
              icon="smileySad_fill"
              iconSize="xxl"
              iconColor="secondary-lighter"
            />
          ),
        }}
      />
    </PerFlexRow>
  )
}
